import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  LayoutGrid, 
  Building, 
  PenTool, 
  Image, 
  MessageSquare, 
  LogOut,
  ChevronDown,
  ChevronRight,
  Plus,
  Images
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [propertiesOpen, setPropertiesOpen] = useState(true);
  const [contentOpen, setContentOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/dashboard" className="text-2xl font-bold text-gray-900">
              RealEstate Pro
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-700 hover:text-gray-900">
                View Site
              </Link>
              <span className="text-sm text-gray-600">Welcome, {user?.username}</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r min-h-screen sticky top-16">
          <div className="p-4 space-y-2">
            {/* Dashboard */}
            <Link
              to="/dashboard"
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive('/dashboard')
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-700 hover:bg-gray-100"
              )}
              data-testid="sidebar-dashboard-link"
            >
              <LayoutGrid className="h-4 w-4" />
              Dashboard
            </Link>

            {/* Properties Section with Dropdown */}
            <div>
              <button
                onClick={() => setPropertiesOpen(!propertiesOpen)}
                className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                data-testid="sidebar-properties-dropdown"
              >
                <div className="flex items-center gap-3">
                  <Building className="h-4 w-4" />
                  Properties
                </div>
                {propertiesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {propertiesOpen && (
                <div className="ml-4 mt-1 space-y-1">
                  <Link
                    to="/dashboard/properties"
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                      isActive('/dashboard/properties')
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-100"
                    )}
                    data-testid="sidebar-all-properties-link"
                  >
                    All Properties
                  </Link>
                  <Link
                    to="/dashboard/properties/create"
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                      isActive('/dashboard/properties/create')
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-100"
                    )}
                    data-testid="sidebar-add-property-link"
                  >
                    <Plus className="h-3 w-3" />
                    Add New
                  </Link>
                </div>
              )}
            </div>

            {/* Content Section with Dropdown */}
            <div>
              <button
                onClick={() => setContentOpen(!contentOpen)}
                className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                data-testid="sidebar-content-dropdown"
              >
                <div className="flex items-center gap-3">
                  <PenTool className="h-4 w-4" />
                  Content
                </div>
                {contentOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {contentOpen && (
                <div className="ml-4 mt-1 space-y-1">
                  <Link
                    to="/dashboard/blogs"
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                      isActive('/dashboard/blogs')
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-100"
                    )}
                    data-testid="sidebar-blogs-link"
                  >
                    Blogs
                  </Link>
                  <Link
                    to="/dashboard/blogs/create"
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                      isActive('/dashboard/blogs/create')
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-100"
                    )}
                    data-testid="sidebar-add-blog-link"
                  >
                    <Plus className="h-3 w-3" />
                    New Blog
                  </Link>
                  <Link
                    to="/dashboard/hero-slides"
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                      isActive('/dashboard/hero-slides')
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-100"
                    )}
                    data-testid="sidebar-hero-slides-link"
                  >
                    <Image className="h-3 w-3" />
                    Hero Slides
                  </Link>
                  <Link
                    to="/dashboard/gallery"
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                      isActive('/dashboard/gallery')
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-100"
                    )}
                    data-testid="sidebar-gallery-link"
                  >
                    <Images className="h-3 w-3" />
                    Gallery
                  </Link>
                </div>
              )}
            </div>

            {/* Inquiries */}
            <Link
              to="/dashboard/inquiries"
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive('/dashboard/inquiries')
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-700 hover:bg-gray-100"
              )}
              data-testid="sidebar-inquiries-link"
            >
              <MessageSquare className="h-4 w-4" />
              Inquiries
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
